import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { contact, site } from "../mock";
import { useLang } from "../i18n";

function LegalShell({ title, subtitle, children }) {
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[14px] font-medium text-[#55575e] transition-colors hover:text-[#2a2b2f]"
        >
          <ArrowLeft className="h-4 w-4" /> {t.legalBack}
        </Link>
        {t.legalGermanNote && (
          <p className="mt-7 rounded-xl border border-slate-200 bg-[#f5f6f8] px-5 py-4 text-[14px] leading-relaxed text-slate-600">
            {t.legalGermanNote}
          </p>
        )}
        <h1 className="mt-7 font-display text-4xl leading-tight text-[#2a2b2f] sm:text-5xl">{title}</h1>
        {subtitle && <p className="mt-4 text-[16px] leading-relaxed text-slate-600">{subtitle}</p>}
        <div className="mt-10 space-y-10">{children}</div>
      </div>
    </section>
  );
}

function Block({ heading, children }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-[#2a2b2f]">{heading}</h2>
      <div className="mt-3 space-y-3 text-[15.5px] leading-[1.8] text-slate-600">{children}</div>
    </div>
  );
}

export function Impressum() {
  return (
    <LegalShell title="Impressum" subtitle="Angaben gemäß § 5 DDG (Digitale-Dienste-Gesetz).">
      <Block heading="Anbieter">
        <p>
          {contact.company}
          <br />
          Inhaber: Nawar Jasem
          <br />
          {contact.street}
          <br />
          {contact.zipCity}
          <br />
          Deutschland
        </p>
      </Block>

      <Block heading="Kontakt">
        <p>
          Telefon:{" "}
          <a className="text-[#55575e] hover:underline" href={`tel:${contact.phoneLink}`}>
            {contact.phoneDisplay}
          </a>
          <br />
          E-Mail:{" "}
          <a className="text-[#55575e] hover:underline" href={`mailto:${contact.email}`}>
            {contact.email}
          </a>
          <br />
          Internet: {site.domain}
        </p>
      </Block>

      <Block heading="Vertretungsberechtigt">
        <p>Nawar Jasem, Inhaber der Pension Günding Gästehaus (Einzelunternehmen).</p>
      </Block>

      <Block heading="Umsatzsteuer-Identifikationsnummer">
        <p>Wird auf Anfrage mitgeteilt.</p>
      </Block>

      <Block heading="Verantwortlich für den Inhalt">
        <p>
          Nawar Jasem, {contact.street}, {contact.zipCity}
        </p>
      </Block>

      <Block heading="Streitschlichtung">
        <p>
          Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit. Wir sind
          nicht verpflichtet und nicht bereit, an einem Streitbeilegungsverfahren vor einer
          Verbraucherschlichtungsstelle teilzunehmen.
        </p>
      </Block>

      <Block heading="Haftung für Inhalte">
        <p>
          Als Dienstanbieter sind wir für eigene Inhalte auf diesen Seiten nach den allgemeinen
          Gesetzen verantwortlich. Wir sind jedoch nicht verpflichtet, übermittelte oder gespeicherte
          fremde Informationen zu überwachen. Eine Haftung ist erst ab dem Zeitpunkt der Kenntnis einer
          konkreten Rechtsverletzung möglich. Bei Bekanntwerden entfernen wir solche Inhalte
          unverzüglich.
        </p>
      </Block>

      <Block heading="Haftung für Links">
        <p>
          Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen
          Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder
          Betreiber verantwortlich. Rechtswidrige Links entfernen wir nach Kenntniserlangung
          unverzüglich.
        </p>
      </Block>

      <Block heading="Urheberrecht">
        <p>
          Die Inhalte und Werke auf diesen Seiten, insbesondere die Fotografien der Pension, unterliegen
          dem deutschen Urheberrecht. Vervielfältigung, Bearbeitung und Verbreitung bedürfen der
          schriftlichen Zustimmung des jeweiligen Rechteinhabers.
        </p>
      </Block>
    </LegalShell>
  );
}

export function Datenschutz() {
  return (
    <LegalShell
      title="Datenschutzerklärung"
      subtitle="Informationen zur Verarbeitung personenbezogener Daten nach Art. 13 DSGVO."
    >
      <Block heading="1. Verantwortliche Stelle">
        <p>
          {contact.company}
          <br />
          Inhaber: Nawar Jasem
          <br />
          {contact.street}
          <br />
          {contact.zipCity}
          <br />
          Telefon: {contact.phoneDisplay}
          <br />
          E-Mail: {contact.email}
        </p>
      </Block>

      <Block heading="2. Allgemeines zur Datenverarbeitung">
        <p>
          Wir verarbeiten personenbezogene Daten nur, soweit dies zur Bereitstellung einer
          funktionsfähigen Website sowie unserer Leistungen erforderlich ist oder Sie eingewilligt
          haben. Rechtsgrundlagen sind insbesondere Art. 6 Abs. 1 lit. a, b und f DSGVO.
        </p>
      </Block>

      <Block heading="3. Server-Logfiles">
        <p>
          Beim Aufruf dieser Website werden durch den Hosting-Anbieter automatisch Informationen
          erfasst, die Ihr Browser übermittelt: Browsertyp und -version, verwendetes Betriebssystem,
          Referrer-URL, Uhrzeit der Serveranfrage und IP-Adresse. Diese Daten dienen der technischen
          Bereitstellung und Sicherheit (Art. 6 Abs. 1 lit. f DSGVO) und werden nach kurzer Zeit
          gelöscht.
        </p>
      </Block>

      <Block heading="4. Kontaktaufnahme und Anfrageformular">
        <p>
          Wenn Sie uns per Telefon, E-Mail oder über das Anfrageformular kontaktieren, verarbeiten wir
          die von Ihnen mitgeteilten Daten (Name, E-Mail-Adresse, ggf. Telefonnummer, Nachricht)
          ausschließlich zur Bearbeitung Ihrer Anfrage – Rechtsgrundlage ist Art. 6 Abs. 1 lit. b bzw.
          lit. f DSGVO. Die Daten werden gelöscht, sobald der Zweck entfällt und keine gesetzlichen
          Aufbewahrungspflichten entgegenstehen.
        </p>
        <p>
          Hinweis: Das Anfrageformular auf dieser Website ist derzeit eine Vorschau. Es versendet keine
          Nachricht und speichert keine Eingaben auf einem Server – Ihre Eingaben verbleiben in Ihrem
          Browser.
        </p>
      </Block>

      <Block heading="5. Cookies">
        <p>
          Diese Website setzt keine Cookies zu Analyse- oder Marketingzwecken ein. Technisch notwendige
          Speichervorgänge dienen allein dem Betrieb der Seite.
        </p>
      </Block>

      <Block heading="6. Google Maps">
        <p>
          Zur Darstellung unseres Standorts binden wir eine Karte von Google Maps ein (Google Ireland
          Limited, Gordon House, Barrow Street, Dublin 4, Irland). Beim Laden der Karte wird Ihre
          IP-Adresse an Google übertragen; eine Übermittlung in Drittländer ist dabei möglich.
          Rechtsgrundlage ist unser berechtigtes Interesse an einer einfachen Auffindbarkeit (Art. 6
          Abs. 1 lit. f DSGVO). Weitere Informationen finden Sie in der Datenschutzerklärung von
          Google.
        </p>
      </Block>

      <Block heading="7. Externe Schriftarten">
        <p>
          Zur einheitlichen Darstellung verwenden wir Schriftarten von Google Fonts. Beim Seitenaufruf
          wird dazu eine Verbindung zu Servern von Google aufgebaut und Ihre IP-Adresse übertragen
          (Art. 6 Abs. 1 lit. f DSGVO).
        </p>
      </Block>

      <Block heading="8. Social-Media-Verlinkungen">
        <p>
          Wir verlinken auf unsere Profile bei Facebook und Instagram. Es werden keine Social-Plugins
          eingebunden – eine Datenübermittlung erfolgt erst, wenn Sie den Link bewusst anklicken.
        </p>
      </Block>

      <Block heading="9. Ihre Rechte">
        <p>
          Sie haben das Recht auf Auskunft (Art. 15), Berichtigung (Art. 16), Löschung (Art. 17),
          Einschränkung der Verarbeitung (Art. 18), Datenübertragbarkeit (Art. 20) sowie Widerspruch
          (Art. 21 DSGVO). Eine erteilte Einwilligung können Sie jederzeit widerrufen. Zudem steht
          Ihnen ein Beschwerderecht bei einer Aufsichtsbehörde zu – für Bayern beim Bayerischen
          Landesamt für Datenschutzaufsicht (BayLDA), Ansbach.
        </p>
      </Block>

      <Block heading="10. SSL-/TLS-Verschlüsselung">
        <p>
          Diese Seite nutzt zum Schutz der Übertragung vertraulicher Inhalte eine SSL-/TLS-
          Verschlüsselung. Eine verschlüsselte Verbindung erkennen Sie am Schloss-Symbol in Ihrem
          Browser.
        </p>
      </Block>
    </LegalShell>
  );
}

export function Agb() {
  return (
    <LegalShell
      title="Buchungs- und Hausordnung"
      subtitle="Die wichtigsten Regelungen für Ihren Aufenthalt in der Pension Günding Gästehaus."
    >
      <Block heading="1. Anfrage und Buchung">
        <p>
          Anfragen sind telefonisch, per E-Mail oder über das Anfrageformular möglich. Eine Buchung
          wird erst mit unserer ausdrücklichen Bestätigung (telefonisch, per E-Mail oder vor Ort)
          verbindlich. Die auf dieser Website genannten Preise sind Ausgangspreise und können je nach
          Zeitraum und Aufenthaltsdauer abweichen.
        </p>
      </Block>

      <Block heading="2. An- und Abreise">
        <p>
          An- und Abreisezeiten stimmen wir individuell mit Ihnen ab. Bitte melden Sie sich bei der
          Ankunft – wir sprechen Deutsch und Englisch.
        </p>
      </Block>

      <Block heading="3. Zahlung">
        <p>
          Die Zahlung erfolgt nach Vereinbarung. Für längere Aufenthalte und Unterkünfte mit Anmeldung
          treffen wir individuelle Vereinbarungen.
        </p>
      </Block>

      <Block heading="4. Hausordnung">
        <p>
          Die Unterkunft ist eine Nichtraucherunterkunft; alle öffentlichen und privaten Bereiche sind
          Nichtraucherzonen, ein ausgewiesener Raucherbereich steht zur Verfügung. Haustiere sind nicht
          gestattet. Die Unterkunft wird ausschließlich an Erwachsene vermietet. Bitte nehmen Sie
          Rücksicht auf die Nachtruhe und behandeln Sie Gemeinschaftsküche, Terrasse und
          Waschgelegenheit sorgsam.
        </p>
      </Block>

      <Block heading="5. Parken">
        <p>
          Private Parkplätze stehen kostenfrei an der Unterkunft zur Verfügung. Eine Reservierung ist
          nicht erforderlich; ein Anspruch auf einen bestimmten Stellplatz besteht nicht.
        </p>
      </Block>

      <Block heading="6. Haftung">
        <p>
          Für eingebrachte Gegenstände haften wir nur im Rahmen der gesetzlichen Bestimmungen. Schäden
          an der Unterkunft sind uns unverzüglich mitzuteilen.
        </p>
      </Block>
    </LegalShell>
  );
}
